import React, { useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid,
  useTheme,
  styled,
  keyframes
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { opacity: 0.6; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.6; transform: scale(0.9); }
`;

const GradientText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(2),
}));

const FloatingNFT = styled(Box)(({ theme }) => ({
  animation: `${float} 6s ease-in-out infinite`,
  position: 'relative',
}));

const GlowEffect = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '80%',
  height: '80%',
  borderRadius: '50%',
  background: `radial-gradient(circle, ${theme.palette.primary.light}40 0%, transparent 70%)`,
  top: '40%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: -1,
  animation: `${pulse} 4s infinite ease-in-out`,
}));

// Card styles for NFT showcase
const NFTCard = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 400,
  height: 400,
  borderRadius: 24,
  overflow: 'hidden',
  boxShadow: '0 20px 80px rgba(94, 53, 177, 0.3)',
  position: 'relative',
  backgroundImage: 'url("https://source.unsplash.com/random/600x800/?digital,art,nft")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)',
  }
}));

const NFTInfo = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  padding: theme.spacing(3),
  color: 'white',
}));

const HeroSection: React.FC = () => {
  const theme = useTheme();
  const animationRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      sx={{
        py: { xs: 10, md: 15 },
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #f9f9ff 0%, #f5f0ff 100%)',
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.primary.light}20 0%, transparent 70%)`,
          top: '-200px',
          right: '-100px',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.secondary.light}30 0%, transparent 70%)`,
          bottom: '-200px',
          left: '-100px',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box>
              <GradientText variant="h1" fontWeight="800">
                Аналитика NFT для инфлюенсеров
              </GradientText>
              <Typography 
                variant="h5" 
                color="text.secondary" 
                sx={{ mb: 4, fontWeight: 400, lineHeight: 1.6 }}
              >
                Отслеживайте продажи ваших NFT-коллекций в реальном времени. Получайте аналитику, которая поможет вам принимать обоснованные решения и увеличивать прибыль.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                startIcon={<TrendingUpIcon />}
                sx={{ 
                  mr: 2, 
                  py: 1.5, 
                  px: 4, 
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                Начать бесплатно
              </Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative', textAlign: 'center' }}>
              <FloatingNFT ref={animationRef}>
                <NFTCard>
                  <NFTInfo>
                    <Typography variant="overline" sx={{ opacity: 0.8 }}>
                      Коллекция "Digital Dreams"
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      Cosmic Voyage #137
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        Текущая цена
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          2.4 ETH
                        </Typography>
                      </Box>
                    </Box>
                  </NFTInfo>
                </NFTCard>
                <GlowEffect />
              </FloatingNFT>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection; 